import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface DrugSearchRequest {
  refYm?: string;
  drugCode?: string;
  drugCodePharma?: string;
  drugCodeMfds?: string;
  drugName?: string;
  pharmaCompanyName?: string;
  reimbursementType?: string;
  contractStatus?: string | boolean;
  dataSourceType?: string;
}

export interface DrugInfoResponse {
  refYm: string;
  /** 의약품 마스터 ID */
  id?: number;
  drugCode: string;
  drugCodeMfds: string;
  drugCodePharma: string;
  drugName: string;
  mainIngredientCode: string;
  pharmaCompanyName: string;
  priceHira: number;
  pricePharma: number;
  reimbursementType: string;
  dataSourceType: string;
}

export interface DrugCodeSearchResponse {
  /** 의약품 마스터 ID. 제약사 ID가 아님 */
  id?: number;
  drugCode: string;
  drugName: string;
  price: number;
  pharmaCompanyId: number;
  pharmaCompanyName: string;
}

const asSearchRecord = (row: unknown): Record<string, unknown> =>
  row && typeof row === 'object' ? (row as Record<string, unknown>) : {};

/**
 * 검색 응답에서 제약사 ID만 고른다.
 * 의약품 `id`와 섞지 않는다. 이름 검색은 코드·명만 오고 ID가 비는 경우가 있다.
 */
export const resolvePharmaCompanyId = (row: unknown): number => {
  const rec = asSearchRecord(row);
  const raw = rec.pharmaCompanyId ?? rec.pharma_company_id;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const normalizeDrugCodeSearchResponse = (
  row: unknown,
): DrugCodeSearchResponse => {
  const rec = asSearchRecord(row);
  return {
    drugCode: String(rec.drugCode ?? rec.drug_code ?? '').trim(),
    drugName: String(rec.drugName ?? rec.drug_name ?? ''),
    price: Number(rec.price ?? 0) || 0,
    pharmaCompanyId: resolvePharmaCompanyId(rec),
    pharmaCompanyName: String(
      rec.pharmaCompanyName ?? rec.pharma_company_name ?? '',
    ),
  };
};

/**
 * 이름 검색 결과에서 가격이 있는 약품을 위로 올린다.
 * 가격 없음(null·0)은 정규화 단계에서 0이 되므로 0 이하를 아래로 보낸다.
 * 같은 그룹 안에서는 서버가 준 순서를 그대로 둔다 (안정 정렬).
 */
export const sortDrugsByPriceAvailability = (
  rows: DrugCodeSearchResponse[],
): DrugCodeSearchResponse[] => {
  const hasPrice = (row: DrugCodeSearchResponse) => (row.price > 0 ? 0 : 1);
  return [...rows].sort((a, b) => hasPrice(a) - hasPrice(b));
};

export interface DrugDetailRequest {
  refYm: string;
  drugCode: string;
}

export interface DrugDetailResponse {
  refYm: string;
  drugCode: string;
  drugCodeMfds: string;
  drugCodePharma: string;
  drugName: string;
  mainIngredientCode: string;
  priceHira: number;
  pricePharma: number;
  unitQuantity: string;
  baseUnit: string;
  reimbursementType: string;
  etcOtcType: string;
  dataSourceType: string;
  outOfStockFlag: boolean;
  outOfStockFrom: string;
  outOfStockTo: string;
  administrationRouteType: string;
  comparatorFlag: boolean;
  registrationStatusType: string;
  bioequivalenceFlag: boolean;
  atcCode: string;
  /** 앱 노출 여부. PATCH `DrugUpdateRequest.exposeFlag` */
  exposeFlag: boolean;
  pharmaCompanyName: string;
  effect: string;
  precaution: string;
  direction: string;
  memo: string;
}

/** GET /admin/drugs/export 응답 */
export interface DrugExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
  refYm: string;
}

export const DRUG_EXPORT_EMPTY_CODE = 'DRUG_EXPORT_EMPTY';

/** 한 달치가 수만 행이라 기본 10초보다 길게 잡는다 */
export const DRUG_EXPORT_TIMEOUT_MS = 900_000;

export const buildDrugSearchParams = (
  searchRequest: DrugSearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.refYm) params.refYm = searchRequest.refYm;
  if (searchRequest.drugCode) params.drugCode = searchRequest.drugCode;
  if (searchRequest.drugCodePharma) {
    params.drugCodePharma = searchRequest.drugCodePharma;
  }
  if (searchRequest.drugCodeMfds) {
    params.drugCodeMfds = searchRequest.drugCodeMfds;
  }
  if (searchRequest.drugName) params.drugName = searchRequest.drugName;
  if (searchRequest.pharmaCompanyName) {
    params.pharmaCompanyName = searchRequest.pharmaCompanyName;
  }
  if (searchRequest.reimbursementType) {
    params.reimbursementType = searchRequest.reimbursementType;
  }
  if (
    searchRequest.contractStatus != null &&
    searchRequest.contractStatus !== false &&
    searchRequest.contractStatus !== ''
  ) {
    params.contractStatus = String(searchRequest.contractStatus);
  }
  if (searchRequest.dataSourceType) {
    params.dataSourceType = searchRequest.dataSourceType;
  }
  return params;
};

/** 조건에 맞는 의약품이 없으면 404 + DRUG_EXPORT_EMPTY */
export const isDrugExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === DRUG_EXPORT_EMPTY_CODE;
};

export const drugService = {
  getDrugs: async (
    searchRequest: DrugSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<DrugInfoResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
      ...buildDrugSearchParams(searchRequest),
    };

    const response = await apiClient.get(createApiPath.drugs.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 의약품 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   * refYm 을 비우면 최신 배포 연월로 추출한다.
   */
  exportDrugs: async (
    searchRequest: DrugSearchRequest,
  ): Promise<DrugExportResponse> => {
    const response = await apiClient.get<DrugExportResponse>(
      createApiPath.drugs.export(),
      {
        params: buildDrugSearchParams(searchRequest),
        timeout: DRUG_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  searchDrugByCode: async (
    drugCode: string,
    refYm: string,
  ): Promise<DrugCodeSearchResponse> => {
    const response = await apiClient.get(createApiPath.drugs.search(), {
      params: { drugCode, refYm },
    });
    return normalizeDrugCodeSearchResponse(response.data);
  },

  searchDrugsByName: async (
    drugName: string,
    refYm: string,
  ): Promise<DrugCodeSearchResponse[]> => {
    const response = await apiClient.get<unknown>(
      createApiPath.drugs.searchByName(),
      { params: { drugName, refYm } },
    );
    return Array.isArray(response.data)
      ? sortDrugsByPriceAvailability(
          response.data.map(normalizeDrugCodeSearchResponse),
        )
      : [];
  },

  searchDrugsByMultipleNames: async (
    drugNames: string[],
    refYm?: string,
  ): Promise<DrugCodeSearchResponse[]> => {
    const response = await apiClient.get<DrugCodeSearchResponse[]>(
      createApiPath.drugs.searchByNames(),
      {
        params: {
          ...(refYm ? { refYm } : {}),
          drugNames,
        },
        paramsSerializer: { indexes: null },
      },
    );
    return Array.isArray(response.data)
      ? response.data.map(normalizeDrugCodeSearchResponse)
      : [];
  },

  searchDrugsByMultipleCodes: async (
    drugCodes: string[],
    refYm?: string,
  ): Promise<DrugCodeSearchResponse[]> => {
    const response = await apiClient.get<DrugCodeSearchResponse[]>(
      createApiPath.drugs.searchByCodes(),
      {
        params: {
          ...(refYm ? { refYm } : {}),
          drugCodes,
        },
        paramsSerializer: { indexes: null },
      },
    );
    return Array.isArray(response.data)
      ? response.data.map(normalizeDrugCodeSearchResponse)
      : [];
  },

  /**
   * 공개된 최신 기준 연월(YYYYMM).
   * 미공개 월로 목록을 조회하면 404(DRUG_DATA_NOT_RELEASED)라 목록 기본값으로 쓴다.
   * 공개된 월이 하나도 없으면 같은 코드로 404.
   */
  getLatestReleasedRefYm: async (): Promise<string> => {
    const response = await apiClient.get<{ refYm: string }>(
      createApiPath.drugs.latestReleasedRefYm(),
    );
    return response.data.refYm;
  },

  getDrugDetail: async (
    request: DrugDetailRequest,
  ): Promise<DrugDetailResponse> => {
    const response = await apiClient.get(createApiPath.drugs.detail(), {
      params: request,
    });
    return response.data;
  },

  updateDrug: async (
    refYm: string,
    drugCode: string,
    data: Record<string, unknown>,
  ): Promise<void> => {
    await apiClient.patch(createApiPath.drugs.update(refYm, drugCode), data);
  },
};
